import { useEffect, useState } from "react";
import { Card, IconNames, IntegratorsLabel, Shop } from "@/types/v2";
import { useStore } from "@/stores/useStore";
import { useCustomerUpdates } from "@/hooks/useCustomerUpdates";
import { useCardMenuActions } from "@/hooks/useCardMenuActions";
import { useRoPanelAnalytics } from "@/hooks/useRoPanelAnalytics";
import { httpsCallables } from "@/repositories/httpsCallables";
import { sentry } from "@/services/sentry";
import { toast } from "@/hooks/useToast";
import { firestoreToDateTime } from "@/lib/utils";
import { getDefaultForceEffectColor } from "@/lib/cards";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { FeatureFlagged } from "@/components/Common/FeatureFlagged";
import { CardActionDialogs } from "@/components/Dashboard/components/Card/CardActionDialogs";

// Must match SYNC_COOLDOWN_MS in integrations/src/card/syncOneRepairOrder.ts
const SYNC_COOLDOWN_MS = 45_000;

export interface RoDetailsHeaderActionsProps {
  cardData: Card;
  shop: Shop;
  integrationLinkUrl?: string;
  onMoveToClick?: (card: Card) => void;
}

export const RoDetailsHeaderActions = ({
  cardData,
  shop,
  integrationLinkUrl,
  onMoveToClick,
}: RoDetailsHeaderActionsProps) => {
  const { tags } = useStore();
  const { isCustomerUpdatesEnabled } = useCustomerUpdates();
  const {
    open,
    setOpen,
    dialogOpen,
    setDialogOpen,
    dialog,
    isLabelShownAsTag,
    duplicatePrefill,
    handleRemoveCard,
    handleMarkComplete,
    handleEditCard,
    handleEditTag,
    handleToggleLabelAsTag,
    handleResetCard,
    handleForceEffect,
    handleDuplicateCard,
    handleOpenDialog,
    handleViewDetails,
    handleMoveToClick,
  } = useCardMenuActions(cardData, shop, tags, onMoveToClick);

  const { logPanelActionUsed } = useRoPanelAnalytics();

  // Tracks the user's own click locally (keyed to the card it applies to) so
  // the cooldown/in-flight spinner start immediately, without waiting on the
  // Firestore round-trip that delivers lastManualSyncAttemptAt back to the
  // client. Keying by cardId prevents this local state from leaking onto a
  // different RO's render when the panel switches cards without unmounting
  // (RoDetailsHeaderActions has no `key` prop in RoDetailsTab, so it stays
  // mounted as the same instance across RO switches).
  const [syncState, setSyncState] = useState<{
    cardId: string;
    clickedAt: number;
    isSyncing: boolean;
  }>();
  const [, setCooldownTick] = useState(0);
  const syncStateForCard =
    syncState?.cardId === cardData.id ? syncState : undefined;
  const isSyncing = syncStateForCard?.isSyncing ?? false;
  const lastManualSyncAttempt = firestoreToDateTime(
    cardData.lastManualSyncAttemptAt
  );
  const cooldownStartedAt = Math.max(
    lastManualSyncAttempt?.getTime() ?? 0,
    syncStateForCard?.clickedAt ?? 0
  );
  const onSyncCooldown =
    cooldownStartedAt > 0 && Date.now() - cooldownStartedAt < SYNC_COOLDOWN_MS;

  // Force a re-render exactly when the active cooldown window expires, so
  // the button re-enables on its own rather than staying stuck disabled
  // until some unrelated card write happens to arrive.
  useEffect(() => {
    if (cooldownStartedAt <= 0) return;
    const msRemaining = cooldownStartedAt + SYNC_COOLDOWN_MS - Date.now();
    if (msRemaining <= 0) return;
    const timeoutId = setTimeout(
      () => setCooldownTick((tick) => tick + 1),
      msRemaining
    );
    return () => clearTimeout(timeoutId);
  }, [cooldownStartedAt]);

  const handleSync = async () => {
    const cardId = cardData.id;
    logPanelActionUsed("sync");
    setSyncState({ cardId, clickedAt: Date.now(), isSyncing: true });
    try {
      await httpsCallables.syncOneRepairOrder({
        shopId: shop.id,
        cardId,
      });
    } catch (error) {
      const isCooldownRejection =
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        (error as { code?: string }).code === "functions/failed-precondition";
      if (!isCooldownRejection) {
        sentry.captureException(error);
      }
      toast({
        variant: "destructive",
        title: isCooldownRejection ? "Still cooling down" : "Sync failed",
        description: isCooldownRejection
          ? "Try again in a moment."
          : error instanceof Error
            ? error.message
            : "An unknown error occurred",
      });
    } finally {
      // Only clear the in-flight flag if this is still the same card's sync
      // — if the panel switched to a different card mid-request, that
      // card's syncState was already keyed to a different cardId (or none),
      // so there's nothing to clear here.
      setSyncState((prev) =>
        prev?.cardId === cardId ? { ...prev, isSyncing: false } : prev
      );
    }
  };

  const integrationLabel = shop.integration
    ? IntegratorsLabel[shop.integration]
    : undefined;

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 py-2 pr-4">
      <CardActionDialogs
        cardData={cardData}
        shop={shop}
        dialogOpen={dialogOpen}
        dialog={dialog}
        setDialogOpen={setDialogOpen}
        handleEditCard={handleEditCard}
        handleEditTag={handleEditTag}
        handleRemoveCard={handleRemoveCard}
        handleMarkComplete={handleMarkComplete}
        handleForceEffect={handleForceEffect}
        duplicatePrefill={duplicatePrefill}
        handleDuplicateCard={handleDuplicateCard}
      />

      <Button
        variant="outline"
        size="sm"
        className="h-8 border-slate-200 text-[13px] font-medium leading-none text-slate-900 hover:bg-slate-50 hover:text-slate-900"
        onClick={(e) => {
          logPanelActionUsed("viewDetails");
          handleViewDetails(e);
        }}
      >
        View Details
      </Button>
      {integrationLinkUrl && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 border-slate-200 text-[13px] font-medium leading-none text-slate-900 hover:bg-slate-50 hover:text-slate-900"
          asChild
        >
          <a href={integrationLinkUrl} target="_blank" rel="noreferrer">
            Open {integrationLabel ?? "Repair Order"}
            <Icon name={IconNames.open} size={14} className="text-slate-500" />
          </a>
        </Button>
      )}
      {cardData.status !== "manuallyCreated" && (
        <FeatureFlagged feature="isPerRoSyncEnabled">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 border-slate-200 text-[13px] font-medium leading-none text-slate-900 hover:bg-slate-50 hover:text-slate-900"
            onClick={handleSync}
            disabled={isSyncing || onSyncCooldown}
            title={
              onSyncCooldown ? "Synced recently — try again shortly" : "Sync"
            }
            type="button"
          >
            <Icon
              name={IconNames.sync}
              size={14}
              className={`text-slate-500 ${isSyncing ? "animate-spin" : ""}`}
            />
          </Button>
        </FeatureFlagged>
      )}
      {onMoveToClick && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 border-slate-200 text-[13px] font-medium leading-none text-slate-900 hover:bg-slate-50 hover:text-slate-900"
          onClick={(e) => {
            logPanelActionUsed("moveTo");
            handleMoveToClick(e);
          }}
          type="button"
          title="Move Card"
        >
          <Icon name={IconNames.move} size={14} className="text-slate-500" />
        </Button>
      )}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger className="flex size-8 cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
          <Icon name="moreVertical" size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="flex flex-col gap-1 p-1">
          <DropdownMenuItem
            onClick={(e) => {
              logPanelActionUsed("viewDetails");
              handleViewDetails(e);
            }}
          >
            <Icon name={IconNames.open} size={14} />
            <span>View Details</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              logPanelActionUsed("markComplete");
              handleOpenDialog("markComplete");
            }}
          >
            <Icon name={IconNames.check} size={14} />
            <span>Mark as Complete</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {cardData.source === "manual" ? (
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                logPanelActionUsed("edit");
                handleOpenDialog("form");
              }}
            >
              <Icon name="edit" size={14} />
              <span>Edit</span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                logPanelActionUsed("tags");
                handleOpenDialog("tags");
              }}
            >
              <Icon name="tags" size={14} />
              <span>Tags</span>
            </DropdownMenuItem>
          )}
          {cardData.source !== "manual" && cardData.label && (
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                logPanelActionUsed("toggleLabelAsTag");
                handleToggleLabelAsTag();
              }}
            >
              <Icon name="tag" size={14} />
              <span>{isLabelShownAsTag ? "Hide Label" : "Show Label"}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              logPanelActionUsed("remove");
              handleOpenDialog("remove");
            }}
          >
            <Icon name="delete" size={14} />
            <span>Remove</span>
          </DropdownMenuItem>
          {(!!cardData.automationAction || !!cardData.manualAction) && (
            <DropdownMenuItem
              onClick={(e) => {
                logPanelActionUsed("resetAutomation");
                handleResetCard(e);
              }}
            >
              <Icon name="undo" size={14} />
              <span>Reset Effects</span>
            </DropdownMenuItem>
          )}
          <FeatureFlagged feature="isForceCardEffectsEnabled">
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                logPanelActionUsed("flashCard");
                handleOpenDialog("flashColor");
              }}
            >
              <Icon name={IconNames.flashColor} size={14} />
              <span>Flash Card</span>
            </DropdownMenuItem>
          </FeatureFlagged>
          <FeatureFlagged feature="isForceCardEffectsEnabled">
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                logPanelActionUsed("wiggleCard");
                handleForceEffect("wiggle", getDefaultForceEffectColor(shop));
              }}
            >
              <Icon name={IconNames.wiggleCard} size={14} />
              <span>Wiggle Card</span>
            </DropdownMenuItem>
          </FeatureFlagged>
          <FeatureFlagged
            feature="isCustomerUpdatesEnabled"
            enabled={isCustomerUpdatesEnabled}
          >
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                logPanelActionUsed("customerUpdate");
                handleOpenDialog("customerUpdate");
              }}
            >
              <Icon name="bell" size={14} />
              <span>Customer Update</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                logPanelActionUsed("customerUpdatesTimeline");
                handleOpenDialog("customerUpdatesTimeline");
              }}
            >
              <Icon name={IconNames.clock} size={14} />
              <span>Customer Updates Timeline</span>
            </DropdownMenuItem>
          </FeatureFlagged>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
